import type { ReactNode } from 'react';
import {
    formatDays,
    formatRatePercent,
    formatRatio,
    formatSignedCurrency,
    formatSignedPercent,
    formatUnsignedCurrency,
    signedToneClass,
    thresholdToneClass
} from '../performanceFormat';
import { formatShortDate, type EquityStats, type PerformanceBasis, type TradeStats } from '../performanceUtils';

interface PerformanceKpiTilesProps {
    equityStats: EquityStats;
    tradeStats: TradeStats;
    basis: PerformanceBasis;
    /** Name of the selected strategy, or "N strategies"; null when the whole portfolio is shown. */
    scopeLabel: string | null;
    /** Open-position P&L of the selected strategies from the latest marks; null when nothing is marked. */
    scopeOpenPnl: number | null;
    scopeOpenCount: number;
}

interface TileProps {
    label: string;
    value: ReactNode;
    detail?: ReactNode;
    tone?: string;
}

const Tile = ({ label, value, detail, tone = 'text-white' }: TileProps) => (
    <div className="min-w-0 rounded-lg border border-zinc-800 bg-[#121212] p-4">
        <div className="text-xs uppercase tracking-wider text-zinc-400">{label}</div>
        <div className={`mt-2 text-2xl font-semibold leading-tight ${tone}`}>{value}</div>
        {detail && <div className="mt-1 text-sm text-zinc-400">{detail}</div>}
    </div>
);

const PerformanceKpiTiles = ({ equityStats, tradeStats, basis, scopeLabel, scopeOpenPnl, scopeOpenCount }: PerformanceKpiTilesProps) => {
    const isScoped = scopeLabel !== null;
    const markToMarket = Math.round((equityStats.pnl - tradeStats.netProfit) * 100) / 100;
    const hasDrawdown = equityStats.drawdownPeakDate !== null && equityStats.drawdownTroughDate !== null;
    const hasMovement = equityStats.bestDay !== null && equityStats.worstDay !== null;

    return (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6">
            <Tile
                detail={
                    isScoped ? (
                        `${equityStats.activeDays} active days · realized plus open-mark changes`
                    ) : (
                        <>
                            <span className={signedToneClass(equityStats.returnPercent)}>
                                {formatSignedPercent(equityStats.returnPercent)}
                            </span>{' '}
                            on {formatUnsignedCurrency(equityStats.startEquity, 0)} start · {equityStats.activeDays} active days
                        </>
                    )
                }
                label={isScoped ? 'Scoped P&L' : basis === 'funding' ? 'Equity change' : 'Period P&L'}
                tone={signedToneClass(equityStats.pnl, 'text-white')}
                value={formatSignedCurrency(equityStats.pnl)}
            />
            <Tile
                detail={
                    isScoped ? (
                        <>
                            Open now <span className={signedToneClass(scopeOpenPnl)}>{formatSignedCurrency(scopeOpenPnl, 0)}</span> across{' '}
                            {scopeOpenCount} position{scopeOpenCount === 1 ? '' : 's'}
                        </>
                    ) : (
                        <>
                            Open marks <span className={signedToneClass(markToMarket)}>{formatSignedCurrency(markToMarket, 0)}</span> ·{' '}
                            {tradeStats.tradeCount} close{tradeStats.tradeCount === 1 ? '' : 's'} in period
                        </>
                    )
                }
                label="Realized"
                tone={signedToneClass(tradeStats.netProfit, 'text-white')}
                value={formatSignedCurrency(tradeStats.netProfit, 0)}
            />
            <Tile
                detail={`${tradeStats.winCount} of ${tradeStats.tradeCount} closed · avg hold ${formatDays(tradeStats.averageHoldDays)}`}
                label="Win rate"
                tone={thresholdToneClass(tradeStats.winRate, 50, 'text-white')}
                value={formatRatePercent(tradeStats.winRate)}
            />
            <Tile
                detail={`won ${formatUnsignedCurrency(tradeStats.grossProfit, 0)} / lost ${formatUnsignedCurrency(tradeStats.grossLoss, 0)}`}
                label="Profit factor"
                tone={thresholdToneClass(tradeStats.profitFactor, 1, 'text-white')}
                value={formatRatio(tradeStats.profitFactor)}
            />
            <Tile
                detail={
                    hasDrawdown
                        ? `${formatSignedCurrency(equityStats.maxDrawdownAmount, 0)} · ${formatShortDate(equityStats.drawdownPeakDate as string)} → ${formatShortDate(
                              equityStats.drawdownTroughDate as string
                          )}`
                        : 'no drawdown in period'
                }
                label="Max drawdown"
                tone={equityStats.maxDrawdownPercent < 0 ? 'text-red-500' : 'text-white'}
                value={formatSignedPercent(equityStats.maxDrawdownPercent)}
            />
            <Tile
                detail={
                    hasMovement
                        ? `${formatShortDate(equityStats.bestDay!.date)} / ${formatShortDate(equityStats.worstDay!.date)} · ${equityStats.greenDays} green · ${
                              equityStats.redDays
                          } red`
                        : 'no movement in period'
                }
                label="Best / worst day"
                value={
                    hasMovement ? (
                        <>
                            <span className="text-profit-green">{formatSignedCurrency(equityStats.bestDayPnl, 0)}</span>{' '}
                            <span className="text-base text-zinc-500">/</span>{' '}
                            <span className="text-red-500">{formatSignedCurrency(equityStats.worstDayPnl, 0)}</span>
                        </>
                    ) : (
                        '—'
                    )
                }
            />
        </div>
    );
};

export default PerformanceKpiTiles;
